import React, { useMemo, useState } from "react";
import { v4 as uuidV4 } from "uuid";
import { AlertCircle, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { AgtechReleaseBreakdownItem } from "@/lib/api/cfr";
import { useBulkSendBatchSubmitter, type BulkSendInstancePayload } from "./useBulkSendBatchSubmitter";
import { BulkSendBatchProgressView } from "./BulkSendBatchProgressView";

interface BulkAgtechReleaseSendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItems: AgtechReleaseBreakdownItem[];
}

export function BulkAgtechReleaseSendDialog({
  open,
  onOpenChange,
  selectedItems,
}: BulkAgtechReleaseSendDialogProps) {
  const { toast } = useToast();
  const [isTestSend, setIsTestSend] = useState(false);
  const { phase, isSubmitting, submit, close } = useBulkSendBatchSubmitter({
    onBeforeRedirect: () => {
      setIsTestSend(false);
      onOpenChange(false);
    },
  });


  // Each report row already carries its exact matched CFR document id,
  // template id, customer id, and suggestion id — there is no template lookup
  // or commodity-level fan-out. A row is only sendable when all four ids are
  // present (structural UI guard, not field-shape revalidation).
  const validationSummary = useMemo(() => {
    const validItems: AgtechReleaseBreakdownItem[] = [];
    const unsendable: AgtechReleaseBreakdownItem[] = [];

    for (const item of selectedItems) {
      const isSendable =
        !!item.customer_id &&
        !!item.suggestion_id &&
        !!item.cfr_document_id &&
        !!item.document_template_id;

      if (isSendable) {
        validItems.push(item);
      } else {
        unsendable.push(item);
      }
    }

    return { validItems, unsendable };
  }, [selectedItems]);

  const commoditySummary = useMemo(() => {
    const summaryMap = new Map<string, number>();
    for (const item of selectedItems) {
      const key = `${item.commodity_type || "unknown"}|${item.crop_year ?? "n/a"}`;
      summaryMap.set(key, (summaryMap.get(key) || 0) + 1);
    }
    return Array.from(summaryMap.entries()).map(([key, count]) => {
      const [commodity, cropYear] = key.split("|");
      return { commodity, cropYear, count };
    });
  }, [selectedItems]);

  const handleClose = () => {
    if (isSubmitting) { return; }
    close();
    setIsTestSend(false);
    onOpenChange(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (validationSummary.validItems.length === 0) {
      toast({
        title: "Cannot Send Contracts",
        description: "No selected items are eligible for sending.",
        variant: "destructive",
      });
      return;
    }

    // Strict 1:1 — one payload per selected row using that row's own ids.
    const payloads: BulkSendInstancePayload[] = validationSummary.validItems.map((item: AgtechReleaseBreakdownItem) => ({
      cfr_document_id: item.cfr_document_id,
      group_id: uuidV4(),
      template_id: item.document_template_id,
      customer_id: item.customer_id,
      suggestion_id: item.suggestion_id,
      is_test_send: isTestSend,
      is_draft: false,
    }));

    await submit(payloads);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { handleClose(); } else { onOpenChange(o); } }}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Bulk Send Agtech Release Contracts</DialogTitle>
        </DialogHeader>

        {phase.kind !== "idle" ? (
          <BulkSendBatchProgressView phase={phase} onClose={handleClose} />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Selected Contracts</Label>
                <div className="text-sm text-muted-foreground">
                  {selectedItems.length} item(s) selected
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="is_test_send">Is Test Send</Label>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_test_send"
                    checked={isTestSend}
                    onCheckedChange={(checked) => setIsTestSend(!!checked)}
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label>Validation Summary</Label>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-600">
                      {validationSummary.validItems.length} ready to send
                    </span>
                  </div>
                  {validationSummary.unsendable.length > 0 && (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                      <span className="text-red-700">
                        {validationSummary.unsendable.length} missing required customer / suggestion / document / template id
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {commoditySummary.length > 0 && (
                <div className="grid gap-2">
                  <Label>Commodity &amp; Crop Year Mix</Label>
                  <div className="grid gap-1 text-sm text-muted-foreground">
                    {commoditySummary.map(({ commodity, cropYear, count }) => (
                      <div key={`${commodity}-${cropYear}`} className="flex justify-between">
                        <span>{`${commodity.toUpperCase()} > ${cropYear}`}</span>
                        <span>{count} item(s)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={validationSummary.validItems.length === 0}
              >
                {validationSummary.validItems.length === 0
                  ? "No Sendable Contracts"
                  : `Send ${validationSummary.validItems.length} Contract(s)`}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
